import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useGameStore } from '../stores/gameStore';
import type { ClientMessage, GambitConfig } from '@wasted-games/shared';

interface LobbyProps {
  sessionCode: string;
  onStart: (config: GambitConfig) => void;
  onLeave: () => void;
  send: (msg: ClientMessage) => void;
}

function Lobby({ sessionCode, onStart, onLeave }: LobbyProps) {
  const players = useGameStore((s) => s.players);
  const isHost = useGameStore((s) => s.isHost);
  const config = useGameStore((s) => s.config);

  const [targetPoints, setTargetPoints] = useState(config.targetPoints || 5);
  const [submissionTime, setSubmissionTime] = useState(config.timeLimitSubmission || 60);
  const [votingTime, setVotingTime] = useState(config.timeLimitVoting || 30);
  const [copied, setCopied] = useState(false);

  // Read from URL params for host config
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTarget = params.get('targetPoints');
    const urlSubTime = params.get('submissionTime');
    const urlVoteTime = params.get('votingTime');
    if (urlTarget) setTargetPoints(Number(urlTarget));
    if (urlSubTime) setSubmissionTime(Number(urlSubTime));
    if (urlVoteTime) setVotingTime(Number(urlVoteTime));
  }, []);

  const joinUrl = `${window.location.origin}/join/${sessionCode}`;
  const connectedPlayers = players.filter((p) => p.isConnected);
  const canStart = isHost && connectedPlayers.length >= 3;

  const handleStart = () => {
    onStart({
      gameType: 'gambit',
      targetPoints,
      timeLimitSubmission: submissionTime,
      timeLimitVoting: votingTime,
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-1">Game Lobby</h2>
          <div className="flex items-center justify-center gap-2">
            <span className="font-mono text-3xl font-black tracking-[0.4em] text-primary">
              {sessionCode}
            </span>
            <button
              className="btn btn-ghost btn-xs"
              onClick={handleCopy}
              title="Copy join link"
            >
              {copied ? (
                <span className="text-success text-sm">Copied!</span>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-center">
          <div className="bg-white p-3 rounded-xl">
            <QRCodeSVG value={joinUrl} size={160} />
          </div>
        </div>

        <div className="card bg-base-200">
          <div className="card-body py-4">
            <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide">
              Players ({players.length})
            </h3>
            <div className="flex flex-wrap gap-2 mt-2">
              {players.map((p) => (
                <div
                  key={p.id}
                  className={`badge badge-lg gap-1 ${
                    p.isHost ? 'badge-primary' : 'badge-neutral'
                  } ${!p.isConnected ? 'opacity-50' : ''}`}
                >
                  {p.name}
                  {p.isHost && <span className="text-xs">host</span>}
                  {!p.isConnected && <span className="text-xs">away</span>}
                </div>
              ))}
            </div>
            {connectedPlayers.length < 3 && (
              <p className="text-warning text-sm mt-2">
                Need {3 - connectedPlayers.length} more player{3 - connectedPlayers.length !== 1 ? 's' : ''} to start
              </p>
            )}
          </div>
        </div>

        {isHost && (
          <div className="card bg-base-200">
            <div className="card-body py-4 space-y-3">
              <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide">
                Settings
              </h3>
              <div className="flex justify-between items-center">
                <span className="text-sm">Points to win</span>
                <span className="font-bold">{targetPoints}</span>
              </div>
              <input
                type="range"
                min={3}
                max={20}
                value={targetPoints}
                onChange={(e) => setTargetPoints(Number(e.target.value))}
                className="range range-primary range-xs"
              />
              <div className="flex justify-between items-center">
                <span className="text-sm">Submission time</span>
                <span className="font-bold">{submissionTime}s</span>
              </div>
              <input
                type="range"
                min={15}
                max={120}
                step={5}
                value={submissionTime}
                onChange={(e) => setSubmissionTime(Number(e.target.value))}
                className="range range-secondary range-xs"
              />
              <div className="flex justify-between items-center">
                <span className="text-sm">Voting time</span>
                <span className="font-bold">{votingTime}s</span>
              </div>
              <input
                type="range"
                min={10}
                max={60}
                step={5}
                value={votingTime}
                onChange={(e) => setVotingTime(Number(e.target.value))}
                className="range range-accent range-xs"
              />
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={onLeave} className="btn btn-ghost flex-1">
            Leave
          </button>
          {isHost && (
            <button
              onClick={handleStart}
              disabled={!canStart}
              className="btn btn-primary flex-[2]"
            >
              Start Game
            </button>
          )}
        </div>

        {!isHost && (
          <p className="text-center text-base-content/40 text-sm">
            Waiting for host to start...
          </p>
        )}
      </div>
    </div>
  );
}

export default Lobby;
